import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Header } from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { formatGs, MODALIDADES, ROLE_LABELS } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Truck, UserCheck, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { SearchBox, FilterSelect, matchQ } from "../components/SearchBox";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100";

export default function Retiros() {
  const { setMobileOpen } = useOutletContext();
  const { user } = useAuth();
  const isStaff = ["admin", "encargado", "encargado_comisionista"].includes(user?.role);
  const isDelivery = ["repartidor", "comisionista", "punto"].includes(user?.role);
  const [retiros, setRetiros] = useState([]);
  const [q, setQ] = useState("");
  const [fEst, setFEst] = useState("pendientes");
  const [repartidores, setRepartidores] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [assign, setAssign] = useState(null);
  const [complete, setComplete] = useState(null);
  const [form, setForm] = useState({ cliente_nombre: "", whatsapp: "", direccion: "", ciudad: "", notas: "" });
  const [cf, setCf] = useState({ destinatario: "", whatsapp: "", ciudad: "", valor_producto: "", costo_envio: "", modalidad: "cod" });

  const load = useCallback(() => { api.get("/retiros").then(({ data }) => setRetiros(data)).catch(() => {}); }, []);
  useEffect(() => { load(); if (isStaff) api.get("/users").then(({ data }) => setRepartidores(data.filter((u) => ["repartidor", "comisionista"].includes(u.role)))).catch(() => {}); }, [load, isStaff]);

  const createRetiro = async (e) => {
    e.preventDefault();
    try { await api.post("/retiros", form); toast.success("Retiro solicitado"); setShowNew(false); setForm({ cliente_nombre: "", whatsapp: "", direccion: "", ciudad: "", notas: "" }); load(); }
    catch (e2) { toast.error(apiErr(e2)); }
  };
  const doAssign = async (repartidor_id) => {
    try { await api.put(`/retiros/${assign.id}/asignar`, { repartidor_id }); toast.success("Repartidor asignado"); setAssign(null); load(); }
    catch (e2) { toast.error(apiErr(e2)); }
  };
  const doComplete = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/retiros/${complete.id}/completar`, { ...cf, valor_producto: Number(cf.valor_producto) || 0, costo_envio: Number(cf.costo_envio) || 0 });
      toast.success("Retiro completado y boleta emitida"); setComplete(null); load();
    } catch (e2) { toast.error(apiErr(e2)); }
  };

  const badge = { solicitado: "bg-slate-100 text-slate-700", asignado: "bg-blue-50 text-blue-700", completado: "bg-emerald-50 text-emerald-700" };

  return (
    <>
      <Header title="Retiros / Pickups" subtitle="Solicitá y coordiná retiros del remitente" onMenu={() => setMobileOpen(true)}
        action={<button onClick={() => setShowNew(true)} data-testid="new-retiro-btn" className="flex items-center gap-2 text-sm font-semibold bg-[#0A1F44] hover:bg-[#132D5C] text-white rounded-lg px-3.5 py-2"><Plus className="h-4 w-4" /> Solicitar retiro</button>} />
      <main className="p-4 sm:p-6 lg:p-8">
        <SearchBox value={q} onChange={setQ} placeholder="Buscar por cliente, dirección, ciudad o WhatsApp…" testid="retiros-search">
          <FilterSelect value={fEst} onChange={setFEst} testid="retiros-filtro-estado" options={[["pendientes", "Pendientes"], ["solicitado", "Solicitados"], ["asignado", "Asignados"], ["completado", "Completados"], ["todos", "Todos"]]} />
        </SearchBox>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {retiros.filter((r) => fEst === "todos" || (fEst === "pendientes" ? r.status !== "completado" : r.status === fEst)).filter(matchQ(q)).map((r) => (
            <div key={r.id} className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm" data-testid={`retiro-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center"><Truck className="h-5 w-5 text-[#3B82F6]" /></div>
                  <div>
                    <div className="font-bold text-[#0A1F44]">{r.cliente_nombre}</div>
                    <div className="text-xs text-[#64748B]">{r.ciudad} · {r.sucursal_origen}</div>
                  </div>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badge[r.status]}`}>{r.status}</span>
              </div>
              <div className="mt-3 text-sm text-[#64748B]">{r.direccion}</div>
              {r.envio_code && <div className="mt-2 text-xs font-mono font-bold text-emerald-700">Boleta: {r.envio_code}</div>}
              <div className="mt-4 flex gap-2">
                {isStaff && r.status !== "completado" && (
                  <button onClick={() => setAssign(r)} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"><UserCheck className="h-4 w-4" /> Asignar</button>
                )}
                {(isDelivery || isStaff) && r.status !== "completado" && (
                  <button onClick={() => { setComplete(r); setCf({ destinatario: "", whatsapp: r.whatsapp, ciudad: "", valor_producto: "", costo_envio: "", modalidad: "cod" }); }} data-testid={`complete-retiro-${r.id}`} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#0A1F44] text-white hover:bg-[#132D5C]"><PackageCheck className="h-4 w-4" /> Completar y emitir boleta</button>
                )}
              </div>
            </div>
          ))}
          {retiros.length === 0 && <div className="col-span-full text-center py-12 text-[#64748B]">No hay retiros registrados.</div>}
        </div>
      </main>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Solicitar retiro</DialogTitle></DialogHeader>
          <form onSubmit={createRetiro} className="space-y-3">
            <input className={input} placeholder="Nombre del remitente" value={form.cliente_nombre} onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })} required data-testid="retiro-nombre-input" />
            <input className={input} placeholder="WhatsApp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} required data-testid="retiro-whatsapp-input" />
            <input className={input} placeholder="Dirección de retiro" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} required data-testid="retiro-direccion-input" />
            <input className={input} placeholder="Ciudad" value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} required data-testid="retiro-ciudad-input" />
            <textarea className={input} placeholder="Notas (opcional)" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            <button className="w-full rounded-lg bg-[#0A1F44] text-white font-semibold py-2.5 text-sm" data-testid="retiro-submit-btn">Solicitar</button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assign} onOpenChange={() => setAssign(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Asignar repartidor</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {repartidores.map((u) => (
              <button key={u.id} onClick={() => doAssign(u.id)} className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 hover:border-[#3B82F6] hover:bg-blue-50/40">
                <div className="font-semibold text-[#0A1F44]">{u.name}</div>
                <div className="text-xs text-[#64748B]">{ROLE_LABELS[u.role]} {u.sucursal ? `· ${u.sucursal}` : ""}</div>
              </button>
            ))}
            {repartidores.length === 0 && <p className="text-sm text-[#64748B] text-center py-6">No hay repartidores. Creá usuarios con rol repartidor.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!complete} onOpenChange={() => setComplete(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Completar retiro · emitir boleta</DialogTitle></DialogHeader>
          <form onSubmit={doComplete} className="space-y-3">
            <input className={input} placeholder="Destinatario" value={cf.destinatario} onChange={(e) => setCf({ ...cf, destinatario: e.target.value })} required data-testid="cf-destinatario" />
            <input className={input} placeholder="WhatsApp destinatario" value={cf.whatsapp} onChange={(e) => setCf({ ...cf, whatsapp: e.target.value })} required />
            <input className={input} placeholder="Ciudad destino" value={cf.ciudad} onChange={(e) => setCf({ ...cf, ciudad: e.target.value })} required />
            <div className="grid grid-cols-2 gap-3">
              <input type="number" className={input} placeholder="Valor producto" value={cf.valor_producto} onChange={(e) => setCf({ ...cf, valor_producto: e.target.value })} />
              <input type="number" className={input} placeholder="Flete" value={cf.costo_envio} onChange={(e) => setCf({ ...cf, costo_envio: e.target.value })} />
            </div>
            <select className={input} value={cf.modalidad} onChange={(e) => setCf({ ...cf, modalidad: e.target.value })}>
              {MODALIDADES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <button className="w-full rounded-lg bg-[#0A1F44] text-white font-semibold py-2.5 text-sm" data-testid="cf-submit">Emitir boleta oficial</button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
